/**
 * Battle VFX + SFX layer — MFix3.4/5/6.
 *
 * Consumes new events from `battle.log` each frame and enqueues short-
 * lived canvas effects (arrow arcs, melee flash, wall-cell impact) + plays
 * synthesized SFX via Web Audio placeholder tones. Michael can swap the
 * SFX names to sourced .wav / .mp3 files later without changing sim code.
 *
 * Design:
 * - Effects are transient; queued with a `startedAt` wall-clock timestamp,
 *   drained after their `dur` elapses.
 * - Only NEW log events (since last processed index) get an effect. Older
 *   events already played their VFX.
 * - Log-index tracking is battle-scoped; when the modal re-opens on a
 *   different battle, `resetEffectTracking(battleId)` clears the cursor.
 *
 * Web Audio: created lazily on first `playSfx` (user-gesture requirement
 * satisfied by the battle-open click). Falls back to silent no-op if the
 * environment doesn't support AudioContext (SSR, test env).
 */

import type { Battle, CombatLogEvent } from '../../sim/battle-state';
import { BATTLESCAPE_WIDTH } from '../../sim/battle-state';

// ---------------------------------------------------------------------------
// Effect queue
// ---------------------------------------------------------------------------

type VfxKind = 'melee-flash' | 'arrow-arc' | 'wall-impact';

interface Vfx {
  readonly kind: VfxKind;
  readonly startedAt: number; // performance.now() when enqueued
  readonly dur: number; // ms
  // Coords in battlescape cell space (float). Rendered by multiplying by cellPx.
  readonly srcX: number;
  readonly srcY: number;
  readonly dstX: number;
  readonly dstY: number;
}

const activeVfx: Vfx[] = [];
let lastProcessedLogIdx = new Map<string, number>(); // battleId → index

/**
 * Reset the log-index cursor for a given battle. Call when the battle-
 * command modal switches to a different battle so we don't skip its
 * initial events.
 */
export function resetEffectTracking(battleId: number | string): void {
  lastProcessedLogIdx.set(String(battleId), 0);
  // Also clear any in-flight effects — they belonged to the old battle.
  activeVfx.length = 0;
}

/**
 * Process new log events since the last frame; enqueue VFX + play SFX.
 * Idempotent within a frame (safe to call from the render loop).
 */
export function processNewBattleEvents(battle: Battle): void {
  const key = String(battle.id);
  const start = lastProcessedLogIdx.get(key) ?? 0;
  for (let i = start; i < battle.log.length; i++) {
    handleEvent(battle, battle.log[i]!);
  }
  lastProcessedLogIdx.set(key, battle.log.length);
}

function handleEvent(battle: Battle, event: CombatLogEvent): void {
  const now = performance.now();
  if (event.kind === 'melee-strike') {
    const attackerPos = findCohortPos(battle, event.attackerId);
    const defenderPos = findCohortPos(battle, event.defenderId);
    if (!attackerPos || !defenderPos) return;
    activeVfx.push({
      kind: 'melee-flash',
      startedAt: now,
      // Session-28 (2026-07-24): extended 220 → 500 ms + wider line +
      // arrowhead in renderMeleeFlash so diagonal strikes are clearly
      // legible to the player. Keeps MFix4.3 Chebyshev-1 combat while
      // making the attack direction obvious every strike.
      dur: 500,
      srcX: attackerPos.x,
      srcY: attackerPos.y,
      dstX: defenderPos.x,
      dstY: defenderPos.y,
    });
    playSfx('melee-hit');
  } else if (event.kind === 'missile-fire') {
    const firerPos = findCohortPos(battle, event.firerId);
    const targetPos = findCohortPos(battle, event.targetId);
    if (!firerPos || !targetPos) return;
    activeVfx.push({
      kind: 'arrow-arc',
      startedAt: now,
      dur: 320,
      srcX: firerPos.x,
      srcY: firerPos.y,
      dstX: targetPos.x,
      dstY: targetPos.y,
    });
    playSfx('arrow-fire');
  } else if (event.kind === 'wall-strike') {
    const firerPos = findCohortPos(battle, event.firerId);
    if (!firerPos) return;
    const wx = event.wallCellIndex % BATTLESCAPE_WIDTH;
    const wy = Math.floor(event.wallCellIndex / BATTLESCAPE_WIDTH);
    activeVfx.push({
      kind: 'wall-impact',
      startedAt: now,
      dur: 420,
      srcX: firerPos.x,
      srcY: firerPos.y,
      dstX: wx,
      dstY: wy,
    });
    playSfx('catapult-boom');
  }
}

function findCohortPos(battle: Battle, cohortId: unknown): { x: number; y: number } | null {
  for (const side of battle.sides) {
    const cof = side.cohorts.get(cohortId as never);
    if (cof) return { x: cof.position.x, y: cof.position.y };
  }
  return null;
}

/**
 * Render currently-active VFX + prune expired. Call once per frame after
 * cohort draws so effects sit on top.
 */
export function renderBattleEffects(ctx: CanvasRenderingContext2D, cellPx: number): void {
  const now = performance.now();
  for (let i = activeVfx.length - 1; i >= 0; i--) {
    const vfx = activeVfx[i]!;
    const elapsed = now - vfx.startedAt;
    if (elapsed >= vfx.dur) {
      activeVfx.splice(i, 1);
      continue;
    }
    const t = elapsed / vfx.dur; // 0..1 progress
    if (vfx.kind === 'melee-flash') renderMeleeFlash(ctx, vfx, t, cellPx);
    else if (vfx.kind === 'arrow-arc') renderArrowArc(ctx, vfx, t, cellPx);
    else if (vfx.kind === 'wall-impact') renderWallImpact(ctx, vfx, t, cellPx);
  }
}

function renderMeleeFlash(ctx: CanvasRenderingContext2D, vfx: Vfx, t: number, cellPx: number): void {
  const cx = (vfx.dstX + 0.5) * cellPx;
  const cy = (vfx.dstY + 0.5) * cellPx;
  const r = cellPx * 0.35 * (1 + t * 0.6);
  const alpha = 0.9 * (1 - t);
  ctx.save();
  ctx.fillStyle = `rgba(255, 220, 130, ${alpha})`;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  // Session-28 (2026-07-24): thicker attack line + arrowhead at target so
  // strike direction (esp. diagonal per MFix4.3 Chebyshev-1 combat) is
  // legible to the player. Longer duration (dur=500ms in handleEvent).
  const ax = (vfx.srcX + 0.5) * cellPx;
  const ay = (vfx.srcY + 0.5) * cellPx;
  ctx.strokeStyle = `rgba(255, 200, 80, ${alpha})`;
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.moveTo(ax, ay);
  ctx.lineTo(cx, cy);
  ctx.stroke();
  // Arrowhead at the target end. Angle from attacker → target; two short
  // strokes back from (cx, cy) at ±30° off the reverse direction.
  const dx = cx - ax;
  const dy = cy - ay;
  const len = Math.hypot(dx, dy);
  if (len > 0.1) {
    const ux = dx / len;
    const uy = dy / len;
    const headLen = cellPx * 0.35;
    const spread = Math.PI / 6; // 30° spread
    const backX = cx - ux * headLen;
    const backY = cy - uy * headLen;
    const perpX = -uy;
    const perpY = ux;
    const tan = Math.tan(spread);
    ctx.fillStyle = `rgba(255, 200, 80, ${alpha})`;
    ctx.beginPath();
    ctx.moveTo(cx, cy);
    ctx.lineTo(backX + perpX * headLen * tan, backY + perpY * headLen * tan);
    ctx.lineTo(backX - perpX * headLen * tan, backY - perpY * headLen * tan);
    ctx.closePath();
    ctx.fill();
  }
  ctx.restore();
}

function renderArrowArc(ctx: CanvasRenderingContext2D, vfx: Vfx, t: number, cellPx: number): void {
  const ax = (vfx.srcX + 0.5) * cellPx;
  const ay = (vfx.srcY + 0.5) * cellPx;
  const bx = (vfx.dstX + 0.5) * cellPx;
  const by = (vfx.dstY + 0.5) * cellPx;
  // Arrow position along parabolic arc (t in [0,1]).
  const x = ax + (bx - ax) * t;
  const straightY = ay + (by - ay) * t;
  const arcHeight = Math.abs(bx - ax) * 0.15 + Math.abs(by - ay) * 0.08;
  const y = straightY - Math.sin(t * Math.PI) * arcHeight;
  ctx.save();
  ctx.strokeStyle = 'rgba(220, 200, 160, 0.9)';
  ctx.lineWidth = 2;
  // Short trail behind the arrow.
  const trailT = Math.max(0, t - 0.08);
  const trailX = ax + (bx - ax) * trailT;
  const trailStraightY = ay + (by - ay) * trailT;
  const trailY = trailStraightY - Math.sin(trailT * Math.PI) * arcHeight;
  ctx.beginPath();
  ctx.moveTo(trailX, trailY);
  ctx.lineTo(x, y);
  ctx.stroke();
  // Arrowhead dot.
  ctx.fillStyle = 'rgba(255, 240, 200, 0.95)';
  ctx.beginPath();
  ctx.arc(x, y, 2.5, 0, Math.PI * 2);
  ctx.fill();
  // Impact flash on target cell in the last 20% of the animation.
  if (t > 0.85) {
    const flashT = (t - 0.85) / 0.15;
    ctx.fillStyle = `rgba(255, 230, 160, ${0.7 * (1 - flashT)})`;
    ctx.beginPath();
    ctx.arc(bx, by, cellPx * 0.3, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

function renderWallImpact(ctx: CanvasRenderingContext2D, vfx: Vfx, t: number, cellPx: number): void {
  const cx = (vfx.dstX + 0.5) * cellPx;
  const cy = (vfx.dstY + 0.5) * cellPx;
  // Dust cloud expanding out from wall cell.
  const alpha = 0.8 * (1 - t);
  const r = cellPx * (0.3 + t * 0.7);
  ctx.save();
  ctx.fillStyle = `rgba(180, 140, 90, ${alpha})`;
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  // Rock chips flying outward.
  ctx.fillStyle = `rgba(120, 80, 50, ${alpha})`;
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    const dx = Math.cos(angle) * cellPx * (0.15 + t * 0.35);
    const dy = Math.sin(angle) * cellPx * (0.15 + t * 0.35);
    ctx.beginPath();
    ctx.arc(cx + dx, cy + dy, 1.5, 0, Math.PI * 2);
    ctx.fill();
  }
  // Central impact flash — brightest at t=0, fades fast.
  if (t < 0.3) {
    const flashT = t / 0.3;
    ctx.fillStyle = `rgba(255, 200, 130, ${0.9 * (1 - flashT)})`;
    ctx.beginPath();
    ctx.arc(cx, cy, cellPx * 0.4, 0, Math.PI * 2);
    ctx.fill();
  }
  ctx.restore();
}

// ---------------------------------------------------------------------------
// SFX — Web Audio placeholder tones
// ---------------------------------------------------------------------------
//
// v0M15.4 (2026-07-29): AudioContext + master-gain ownership moved to
// `src/client/audio.ts` so the Settings modal's SFX volume + mute can
// drive playback in real time. Playback graph is now:
//     osc → per-SFX envelope gain → master gain (settings-driven) → destination
// The per-SFX envelope shapes attack/decay; the master gain applies
// user volume + mute.

type SfxName = 'melee-hit' | 'arrow-fire' | 'catapult-boom';

import { ensureAudioContext, getSfxMasterGain } from '../audio';

/**
 * Play a named SFX. Synthesized placeholder tones — swap for sourced
 * audio files once Michael picks a library.
 */
export function playSfx(name: SfxName): void {
  const ctx = ensureAudioContext();
  const master = getSfxMasterGain();
  if (!ctx || !master) return;
  if (name === 'melee-hit') {
    synthTone(ctx, master, {
      freq: 200,
      freqEnd: 120,
      dur: 0.08,
      type: 'square',
      gain: 0.15,
    });
  } else if (name === 'arrow-fire') {
    synthTone(ctx, master, {
      freq: 850,
      freqEnd: 400,
      dur: 0.14,
      type: 'sine',
      gain: 0.08,
    });
  } else if (name === 'catapult-boom') {
    synthTone(ctx, master, {
      freq: 90,
      freqEnd: 55,
      dur: 0.22,
      type: 'sine',
      gain: 0.22,
    });
  }
}

interface ToneParams {
  readonly freq: number;
  readonly freqEnd: number;
  readonly dur: number; // seconds
  readonly type: OscillatorType;
  readonly gain: number;
}

function synthTone(ctx: AudioContext, master: GainNode, params: ToneParams): void {
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  osc.type = params.type;
  osc.frequency.setValueAtTime(params.freq, now);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, params.freqEnd), now + params.dur);
  const gainNode = ctx.createGain();
  gainNode.gain.setValueAtTime(params.gain, now);
  gainNode.gain.exponentialRampToValueAtTime(0.0001, now + params.dur);
  osc.connect(gainNode);
  gainNode.connect(master);
  osc.start(now);
  osc.stop(now + params.dur + 0.01);
}
